'use client';

import './index.css';

import { storyblokEditable } from '@storyblok/react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/button';
import { MaxWidth } from '@/components/max-width';

import { hasText, render } from '@/helpers/storyblok/rich-text';
import { markdownToHtml } from '@/helpers/string';

const DEFAULT_LEADERS = 1;
const DEFAULT_SALARY = 75000;
const SAVINGS_PER_LEADER = 36000;

const numberFormatter = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 0,
});

const formatEuro = (value) => `${numberFormatter.format(value)} €`;

export const TeamsRoiClient = ({ blok }) => {
  const [leaders, setLeaders] = useState(DEFAULT_LEADERS);
  const [salary, setSalary] = useState(String(DEFAULT_SALARY));

  const savings = useMemo(() => {
    const parsed = Number.parseInt(salary, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(leaders * SAVINGS_PER_LEADER * (parsed / DEFAULT_SALARY));
  }, [leaders, salary]);

  return (
    <div {...storyblokEditable(blok)} className="teams-roi">
      <MaxWidth>
        <div className="teams-roi__wrap">
          <div className="teams-roi__card teams-roi__card--team">
            <p className="teams-roi__card__title">Votre équipe</p>

            <div className="teams-roi__fields">
              <div className="teams-roi__field">
                <label
                  className="teams-roi__field__label"
                  htmlFor="teams-roi-leaders"
                >
                  Nombre de leaders marketing
                </label>

                <div className="teams-roi__counter">
                  <button
                    type="button"
                    className="teams-roi__counter__button"
                    aria-label="Diminuer"
                    onClick={() => setLeaders((n) => Math.max(1, n - 1))}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 8h10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>

                  <input
                    id="teams-roi-leaders"
                    type="text"
                    inputMode="numeric"
                    className="teams-roi__counter__value"
                    value={leaders}
                    onChange={(event) => {
                      const next = Number.parseInt(
                        event.target.value.replace(/[^0-9]/g, ''),
                        10,
                      );
                      setLeaders(Number.isFinite(next) && next > 0 ? next : 1);
                    }}
                  />

                  <button
                    type="button"
                    className="teams-roi__counter__button"
                    aria-label="Augmenter"
                    onClick={() => setLeaders((n) => n + 1)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 3v10M3 8h10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="teams-roi__field">
                <label
                  className="teams-roi__field__label"
                  htmlFor="teams-roi-salary"
                >
                  Salaire moyen annuel brut
                </label>

                <input
                  id="teams-roi-salary"
                  type="text"
                  inputMode="numeric"
                  className="teams-roi__salary"
                  placeholder="XXXX €"
                  value={salary}
                  onChange={(event) =>
                    setSalary(event.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              </div>
            </div>
          </div>

          <div className="teams-roi__card teams-roi__card--savings">
            <div className="teams-roi__card__body">
              <p className="teams-roi__card__heading">
                Vous économisez par an
                <br />
                avec UnlockM Teams
              </p>

              <p className="teams-roi__card__amount">
                {savings > 0 ? formatEuro(savings) : 'XXXX €'}
              </p>

              <p className="teams-roi__card__note">
                Basé sur 8h
                <br />
                économisées/semaine/leader × salaire chargé
              </p>
            </div>
          </div>

          <div
            {...storyblokEditable(blok)}
            className="teams-roi__card teams-roi__card--cta"
          >
            <div className="teams-roi__card__body">
              {blok.title ? (
                <p
                  className="teams-roi__card__heading"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(blok.title),
                  }}
                />
              ) : null}

              {hasText(blok.description) ? (
                <div className="teams-roi__card__description">
                  {render(blok.description)}
                </div>
              ) : null}
            </div>

            {blok.cta?.length > 0 ? (
              <div className="teams-roi__card__cta">
                {blok.cta.map((link) => (
                  <Button
                    key={link._uid}
                    {...storyblokEditable(link)}
                    theme="white"
                    link={link.link}
                  >
                    {link.title}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </MaxWidth>
    </div>
  );
};
